import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Check, Eye, Layers, TrendingUp, Users, Star, ChevronRight, ExternalLink } from 'lucide-react';
import { SEO } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO = "'IBM Plex Mono', monospace";

const VECTORS = [
  {
    id: 'v1', code: 'V₁', label: 'Craft & Tools', max: '25 pts', color: 'bg-blue-400',
    desc: 'Your professional toolkit — the depth and range of the software, techniques, and disciplines that define your practice.',
    factors: ['Software proficiency (1–5 scale)', 'Manual creative skills range', 'Technical tool coverage', 'Specialisation depth'],
  },
  {
    id: 'v2', code: 'V₂', label: 'Aesthetic Identity', max: '40 pts', color: 'bg-[hsl(var(--primary))]',
    desc: 'The most significant pillar — measuring the coherence and richness of your creative world. Evaluated from your portfolio visuals and the aesthetic language you inhabit.',
    factors: ['Portfolio visual direction', 'Style DNA from your Flence profile', 'Aesthetic signature mapping', 'Creative archetype classification'],
  },
  {
    id: 'v3', code: 'V₃', label: 'Industry Presence', max: '20 pts', color: 'bg-green-400',
    desc: 'Your standing within the fashion world — verified influence, peer recognition, and professional authority. Not follower counts, but real craft credibility.',
    factors: ['Professional authority index', 'Portfolio engagement & reach', 'Peer endorsements', 'Profile completeness'],
  },
  {
    id: 'v4', code: 'V₄', label: 'Availability & Fit', max: '15 pts', color: 'bg-gray-400',
    desc: 'Practical alignment — your location, availability, and flexibility to thrive in the right environment. Often the deciding factor between equally matched profiles.',
    factors: ['Current availability status', 'Openness to relocation', 'Work mode flexibility (Remote/Hybrid/On-site)', 'Employment type preferences'],
  },
];

const VISIBILITY_ITEMS = [
  { visible: true,  label: 'Name, role, seniority, location' },
  { visible: true,  label: 'Career visibility profile (4 pillars)' },
  { visible: true,  label: 'Portfolio preview (top 3 items)' },
  { visible: true,  label: 'Style DNA tags & aesthetic profile' },
  { visible: true,  label: 'Software skills & tool proficiency' },
  { visible: true,  label: 'Work experience & education' },
  { visible: true,  label: 'Availability & work preferences' },
  { visible: true,  label: 'Languages spoken' },
  { visible: false, label: 'Contact details (email, phone)' },
  { visible: false, label: 'Personal address' },
  { visible: false, label: 'Financial history' },
];

// Talent-facing FAQ — rendered as visible, crawlable content AND emitted as
// FAQPage structured data for SEO + Answer Engine Optimisation (AEO).
const TALENT_FAQS = [
  {
    q: 'How do I get hired through FashionSpyder?',
    a: 'FashionSpyder does not host candidate sign-ups directly. You build your professional profile on Flence.com — the verified creative community FashionSpyder draws from. Once your Flence profile is live, fashion HR teams discover, evaluate and contact you through FashionSpyder. Hiring is initiated by brands and confirmed by you on Flence.',
  },
  {
    q: 'Does FashionSpyder charge fashion professionals?',
    a: 'No. FashionSpyder never charges talent — there is no fee to be discovered, evaluated, or contacted here; brands pay for access to the intelligence platform. To be found, you do need an active, verified profile on Flence, which is a separate Flence membership/subscription. In short: your Flence profile is where your career lives, and FashionSpyder adds no cost on top of it.',
  },
  {
    q: 'Who can see my profile, and is my contact information private?',
    a: 'Only verified HR teams at fashion houses can see your professional profile. Your personal contact details — email, phone, address — remain private and are only revealed when you directly accept a contact request through Flence.',
  },
  {
    q: 'What makes a profile rank higher and get discovered first?',
    a: 'Four professionally weighted pillars: Aesthetic Identity (your portfolio — the most heavily weighted, 40 pts), Craft & Tools (25 pts), Industry Presence (20 pts), and Availability & Fit (15 pts). A complete profile, strong portfolio imagery, immediate availability, and peer endorsements all increase your Visibility Index.',
  },
  {
    q: 'What is the Career Visibility Engine?',
    a: 'It is the scoring system that builds your presence in front of hiring teams. It combines your portfolio, style identity, skills, experience and availability into a Visibility Index from 0 to 100, surfacing the strongest profiles first for any given role.',
  },
];

const ForTalentFAQ = () => (
  <section className="py-20 lg:py-28 px-6 lg:px-20 bg-white border-t border-border" id="talent-faq">
    <div className="max-w-[900px] mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3" style={{ fontFamily: MONO }}>Common Questions</p>
      <h2 className="text-4xl lg:text-5xl font-semibold mb-12" style={{ fontFamily: SERIF }}>For fashion professionals</h2>
      <div className="border border-border bg-white divide-y divide-border px-6">
        {TALENT_FAQS.map(item => (
          <div key={item.q} className="py-6" data-testid="talent-faq-item">
            <h3 className="font-semibold text-foreground mb-2 leading-snug">{item.q}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-muted-foreground mt-8">
        More questions? Read the full <Link to="/faq" className="text-[hsl(var(--primary))] font-medium hover:underline">FAQ</Link>,
        learn <Link to="/about" className="text-[hsl(var(--primary))] font-medium hover:underline">about FashionSpyder</Link>, or{' '}
        <a href="https://flence.com" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] font-medium hover:underline">create your profile on Flence</a>.
      </p>
    </div>
  </section>
);

const ForTalent = () => {
  const navigate = useNavigate();
  const [openVector, setOpenVector] = useState(null);

  const talentFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: TALENT_FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="For Fashion Professionals — Get Discovered & Hired by Top Houses"
        description="Get discovered and hired by premium fashion houses. FashionSpyder reads your verified Flence profile and puts it in front of brand HR teams. FashionSpyder charges talent nothing — you just need an active Flence membership to be found across craft, aesthetic identity, industry presence and availability."
        path="/for-talent"
        schema={talentFaqSchema}
      />
      {/* Hero */}
      <section className="py-24 lg:py-32 px-6 lg:px-20 border-b border-border relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-[hsl(var(--primary)/0.35)]" />
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground border border-border px-3 py-1.5" style={{ fontFamily: MONO }}>
                <span className="w-1.5 h-1.5 bg-[hsl(var(--primary))]" />
                For Fashion Professionals
              </span>
              <h1 className="text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-[-0.03em]" style={{ fontFamily: SERIF }}>
                Be found by the<br />right houses.
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                FashionSpyder is an intelligence layer used by HR teams at fashion houses to discover, rank, and hire talent from the Flence community. Your Flence profile is your data — here's exactly how we evaluate it.
              </p>
              <div className="flex gap-3">
                <a
                  href="https://flence.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-medium text-white"
                  style={{ background: 'hsl(43 74% 53%)', border: '1px solid hsl(43 74% 42%)' }}
                  data-testid="talent-join-flence-btn"
                >
                  Build my profile on Flence <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-base border border-border text-foreground hover:border-foreground transition-all"
                >
                  How it works
                </button>
              </div>
            </div>

            {/* Score preview card */}
            <div className="border border-border p-6 bg-[hsl(var(--secondary))]">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4" style={{ fontFamily: MONO }}>Your Visibility Snapshot</p>
              <div className="space-y-3">
                {VECTORS.map(v => (
                  <div key={v.id}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground" style={{ fontFamily: MONO }}>{v.code} {v.label}</span>
                      <span className="font-medium text-muted-foreground" style={{ fontFamily: MONO }}>{v.max}</span>
                    </div>
                    <div className="h-2 bg-white border border-border overflow-hidden">
                      <div className={`h-full ${v.color}`} style={{ width: '0%' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>Visibility Index</p>
                <p className="text-2xl font-bold" style={{ fontFamily: SERIF }}>— / 100</p>
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">Complete your Flence profile to activate your presence</p>
            </div>
          </div>
        </div>
      </section>

      {/* Powered by Flence — hiring flow intro */}
      <section className="py-16 lg:py-20 px-6 lg:px-20 bg-[hsl(var(--secondary))] border-b border-border">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground" style={{ fontFamily: MONO }}>Powered by Flence</p>
            <h2 className="text-3xl lg:text-4xl font-semibold leading-tight" style={{ fontFamily: SERIF }}>
              Your profile lives on Flence. The houses find you here.
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-2xl">
              <a href="https://flence.com" target="_blank" rel="noreferrer" className="text-foreground font-medium underline decoration-[hsl(var(--primary))] underline-offset-2">Flence</a> is
              the verified portfolio and career community for fashion creatives. FashionSpyder is the intelligence layer that
              premium brand HR teams use to discover and hire from that community. You build and own your profile on Flence —
              FashionSpyder reads it (with your consent), evaluates it across four professional pillars, and surfaces you to the
              right hiring teams. <strong className="text-foreground">FashionSpyder charges you nothing</strong> (an active Flence membership is required), and your contact details stay
              private until you accept a request.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <a
              href="https://flence.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-medium text-white"
              style={{ background: 'hsl(43 74% 53%)', border: '1px solid hsl(43 74% 42%)' }}
              data-testid="talent-flence-hiring-btn"
            >
              Get hired — start on Flence <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={() => document.getElementById('talent-faq')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base border border-border text-foreground hover:border-foreground transition-all bg-white"
            >
              Read the talent FAQ
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 lg:py-28 px-6 lg:px-20 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3" style={{ fontFamily: MONO }}>The Process</p>
          <h2 className="text-4xl lg:text-5xl font-semibold mb-14" style={{ fontFamily: SERIF }}>
            How your profile gets discovered
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'You build on Flence', desc: 'Your portfolio, style identity, experience, and availability live on Flence. FashionSpyder reads this data — you own it entirely.' },
              { num: '02', title: 'We build your presence', desc: 'Four pillars of your professional identity are combined to form a Career Visibility Profile — putting the right opportunities in front of you.' },
              { num: '03', title: 'The right houses find you', desc: 'Fashion HR teams use FashionSpyder to discover, filter, and match talent to specific roles. The strongest profiles are surfaced first.' },
            ].map(step => (
              <div key={step.num} className="border border-border p-6">
                <p className="text-4xl font-bold text-muted-foreground/20 mb-4" style={{ fontFamily: SERIF }}>{step.num}</p>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* V-vectors detail */}
      <section className="py-20 lg:py-28 px-6 lg:px-20 bg-[hsl(var(--secondary))] border-y border-border">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3" style={{ fontFamily: MONO }}>Visibility Engine</p>
          <h2 className="text-4xl lg:text-5xl font-semibold mb-4" style={{ fontFamily: SERIF }}>The Career Visibility Engine</h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-xl">
            Your visibility is built from four professionally weighted pillars. Here's exactly what shapes your presence in front of fashion's top hiring teams.
          </p>
          <div className="space-y-4">
            {VECTORS.map(v => (
              <div key={v.id} className="bg-white border border-border overflow-hidden">
                <button
                  className="w-full flex items-center gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenVector(openVector === v.id ? null : v.id)}
                  data-testid={`vector-accordion-${v.id}`}
                >
                  <div className={`w-2 h-10 flex-shrink-0 ${v.color}`} />
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-bold" style={{ fontFamily: MONO }}>{v.code}</span>
                      <span className="font-semibold">{v.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{v.desc}</p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-2xl font-bold" style={{ fontFamily: SERIF }}>{v.max}</span>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${openVector === v.id ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                {openVector === v.id && (
                  <div className="px-6 pb-6 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed mt-4 mb-4">{v.desc}</p>
                    <ul className="space-y-2">
                      {v.factors.map(f => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
                          <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visibility */}
      <section className="py-20 lg:py-28 px-6 lg:px-20">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3" style={{ fontFamily: MONO }}>Data & Privacy</p>
            <h2 className="text-4xl font-semibold mb-6" style={{ fontFamily: SERIF }}>What HR teams can see</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Your professional profile is visible to verified HR teams. Personal contact details are protected and are only shared when you directly accept a contact request through Flence.
            </p>
            <a
              href="https://flence.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[hsl(var(--primary))] font-medium"
            >
              Manage your privacy on Flence <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="border border-border">
            <div className="px-5 py-3 border-b border-border bg-[hsl(var(--secondary))]">
              <p className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>Profile Visibility Checklist</p>
            </div>
            <div className="divide-y divide-border">
              {VISIBILITY_ITEMS.map(item => (
                <div key={item.label} className="flex items-center gap-3 px-5 py-3">
                  <div className={`w-4 h-4 flex-shrink-0 flex items-center justify-center ${item.visible ? 'bg-green-100' : 'bg-red-50'}`}>
                    {item.visible
                      ? <Check className="w-3 h-3 text-green-600" />
                      : <span className="text-red-400 text-xs font-bold">✕</span>}
                  </div>
                  <span className={`text-sm ${item.visible ? 'text-foreground' : 'text-muted-foreground'}`}>{item.label}</span>
                  {!item.visible && <span className="ml-auto text-xs text-muted-foreground border border-border px-1.5 py-0.5" style={{ fontFamily: MONO }}>Protected</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tips to improve score */}
      <section className="py-20 px-6 lg:px-20 bg-[hsl(var(--secondary))] border-y border-border">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3" style={{ fontFamily: MONO }}>Maximize Your Visibility</p>
          <h2 className="text-4xl font-semibold mb-10" style={{ fontFamily: SERIF }}>How to be discovered first</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Layers className="w-5 h-5" />, title: 'Complete your profile', desc: 'A complete Flence profile unlocks all 4 visibility pillars. Incomplete profiles remain largely undiscovered.' },
              { icon: <Star className="w-5 h-5" />, title: 'Showcase your portfolio', desc: 'Aesthetic Identity carries the highest weight. Strong portfolio imagery is the most impactful thing you can add.' },
              { icon: <TrendingUp className="w-5 h-5" />, title: 'Stay available', desc: 'Setting "Immediate" availability puts you at the top of active searches. Update it whenever your situation changes.' },
              { icon: <Users className="w-5 h-5" />, title: 'Gather endorsements', desc: 'Peer endorsements build your Industry Presence pillar. Reach out to collaborators on Flence to vouch for your work.' },
            ].map(tip => (
              <div key={tip.title} className="bg-white border border-border p-5">
                <div className="w-8 h-8 border border-border flex items-center justify-center text-muted-foreground mb-4">{tip.icon}</div>
                <h3 className="font-semibold mb-2">{tip.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talent FAQ — crawlable + FAQPage schema */}
      <ForTalentFAQ />

      {/* CTA */}
      <section className="py-28 px-6 lg:px-20 bg-foreground text-background">
        <div className="max-w-[700px] mx-auto text-center space-y-6">
          <h2 className="text-5xl font-semibold" style={{ fontFamily: SERIF }}>
            Ready to be discovered?
          </h2>
          <p className="text-background/70 text-lg leading-relaxed">
            Build your professional profile on Flence and let your work speak to the houses that matter.
          </p>
          <a
            href="https://flence.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-foreground bg-white"
            data-testid="talent-join-flence-cta"
          >
            Start on Flence <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ForTalent;
